import { useMemo, useEffect, useState, ReactNode } from 'react';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { createAppTheme, type AccentColorKey } from '@/theme';
import { useUserPreferences, type ThemeMode } from '@/contexts/UserPreferencesContext';
import type { PaletteMode } from '@mui/material/styles';

const THEME_CACHE_KEY = 'nexo.theme.mode';
const ACCENT_CACHE_KEY = 'nexo.theme.accent';

function useSystemColorScheme(): PaletteMode {
  const [mode, setMode] = useState<PaletteMode>(() =>
    window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light',
  );

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setMode(e.matches ? 'dark' : 'light');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return mode;
}

function resolveMode(preference: ThemeMode, system: PaletteMode): PaletteMode {
  if (preference === 'light' || preference === 'dark') return preference;
  return system;
}

export function ThemePreferenceProvider({ children }: { children: ReactNode }) {
  const { theme: themePreference, accentColor, isLoading } = useUserPreferences();
  const systemMode = useSystemColorScheme();

  // Use localStorage cache while API is loading to avoid color flash
  const cachedMode = localStorage.getItem(THEME_CACHE_KEY) as ThemeMode | null;
  const cachedAccent = localStorage.getItem(ACCENT_CACHE_KEY) as AccentColorKey | null;

  const effectiveMode = isLoading && cachedMode ? cachedMode : themePreference;
  const effectiveAccent = isLoading && cachedAccent ? cachedAccent : (accentColor as AccentColorKey);

  const resolvedMode = resolveMode(effectiveMode, systemMode);

  // Persist to localStorage when API values arrive
  useEffect(() => {
    if (!isLoading) {
      localStorage.setItem(THEME_CACHE_KEY, themePreference);
      localStorage.setItem(ACCENT_CACHE_KEY, accentColor);
    }
  }, [isLoading, themePreference, accentColor]);

  const theme = useMemo(
    () => createAppTheme(resolvedMode, effectiveAccent),
    [resolvedMode, effectiveAccent],
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
}
